import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, Label,
} from "recharts";
import {
  Briefcase, LogOut, TrendingUp, DollarSign, AlertTriangle,
  Activity, Target, ChevronRight, Loader2, Gauge, Sparkles,
  BarChart3, Zap, Layers, FileText, Check, Plug, Settings,
} from "lucide-react";
import { useCSuiteAuth } from "../../state/CSuiteAuthContext";
import { csuiteApi } from "../../lib/csuiteApi";
import { useLiveUpdates } from "../../lib/useLiveUpdates";
import { api } from "../../lib/api";
import D365Cockpit from "./platinum/D365Cockpit";
import OracleCockpit from "./platinum/OracleCockpit";
import Scorecard from "./platinum/Scorecard";
import ValueRealization from "./platinum/ValueRealization";
import InsightsFeed from "./platinum/InsightsFeed";
import HanaCockpit from "./platinum/HanaCockpit";
import AICopilot from "./platinum/AICopilot";
import PaywallCard from "./platinum/PaywallCard";
import { EnterprisePanel } from "./platinum/EnterprisePanel";
import Logo from "../Logo";
import ConnectionsPanel from "./platinum/ConnectionsPanel";
import ReferralsCard from "./platinum/ReferralsCard";
import PilotStatusBanner from "./platinum/PilotStatusBanner";
import SwitchPlanCard from "../SwitchPlanCard";
import ExecutiveDiagnosticsCard from "./ExecutiveDiagnosticsCard";
import LiveUpdatedPill from "./LiveUpdatedPill";
import { DemoBanner, useDemoGate } from "./DemoBanner";
import GuidedTour from "./GuidedTour";
import { generateBoardPack } from "../../lib/boardPack";
import { trackEvent } from "../../lib/analytics";

// Platinum-only quick download button — 20-slide portfolio PPT built entirely
// client-side from live rollup + platinum endpoints. Shows a brief success
// tick + toast when the file downloads. Errors bubble via alert() to keep
// the header lean.
function BoardPackButton() {
  const [busy, setBusy] = React.useState(false);
  const [ok, setOk] = React.useState(false);
  const [hasTemplate, setHasTemplate] = React.useState(false);
  const [useTemplate, setUseTemplate] = React.useState(false);

  React.useEffect(() => {
    api.myTemplates()
      .then(r => setHasTemplate(!!r?.unlocked && (r?.templates || []).some(t => t.kind === "pptx")))
      .catch(() => setHasTemplate(false));
  }, []);

  const run = async () => {
    if (busy) return;
    setBusy(true); setOk(false);
    try {
      await generateBoardPack({ useTemplate: useTemplate && hasTemplate });
      setOk(true);
      trackEvent("sample_board_pack_generated", { with_template: !!(useTemplate && hasTemplate) });
      setTimeout(() => setOk(false), 3000);
    } catch (e) {
      alert(`Board Pack generation failed: ${e.message || e}`);
    } finally { setBusy(false); }
  };
  return (
    <div className="inline-flex items-center gap-2" data-testid="board-pack-controls">
      {hasTemplate && (
        <label className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500 cursor-pointer" title="Merge your company PPTX theme (colours + fonts + master media) into the pack">
          <input type="checkbox" checked={useTemplate} onChange={e => setUseTemplate(e.target.checked)} data-testid="board-pack-use-template"/> My template
        </label>
      )}
      <button className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
              onClick={run} disabled={busy}
              data-testid="btn-generate-board-pack"
              title="Download a 20-slide portfolio PPT">
        {busy ? <Loader2 size={13} className="animate-spin"/> : ok ? <Check size={13}/> : <FileText size={13}/>}
        {busy ? "Generating…" : ok ? "Downloaded" : "Board Pack"}
      </button>
    </div>
  );
}

const PIE_COLORS = ["#0369A1", "#0EA5E9", "#06B6D4", "#22D3EE", "#67E8F9", "#7DD3FC"];
const HEALTH_COLORS = { "On Track": "#16A34A", "At Risk": "#D97706", "Off Track": "#DC2626", "Other": "#71717A" };
const fmtMoney = (n) => "$" + Number(n || 0).toLocaleString();

// ─── small primitives ─────────────────────────────────────────────────────
function KPI({ label, value, sub, tone = "neutral", icon, testid }) {
  const toneClasses = {
    green: "text-emerald-600", amber: "text-amber-600",
    red: "text-red-600", blue: "text-sky-700", neutral: "text-zinc-900",
  };
  return (
    <div className="bg-white border border-[var(--border)] rounded-sm p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">{label}</div>
        {icon && <div className="text-zinc-400">{icon}</div>}
      </div>
      <div className={`font-display text-3xl font-extrabold tracking-tighter ${toneClasses[tone]}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function Section({ title, eyebrow, children, right }) {
  return (
    <section className="mb-8">
      <div className="flex items-end justify-between mb-3">
        <div>
          {eyebrow && <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{eyebrow}</div>}
          <h2 className="font-display text-xl font-bold tracking-tight text-zinc-900 mt-1">{title}</h2>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function Pill({ tone, children }) {
  const cls = {
    green: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-800",
    red:   "bg-red-100 text-red-700",
    blue:  "bg-sky-100 text-sky-700",
    neutral:"bg-zinc-100 text-zinc-700",
  }[tone] || "bg-zinc-100 text-zinc-700";
  return <span className={`inline-block px-2 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-wider ${cls}`}>{children}</span>;
}

// ─── Main dashboard ──────────────────────────────────────────────────────
export default function CSuiteDashboard() {
  const { user, logout } = useCSuiteAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);
  const [view, setView] = useState("portfolio");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [lastPushWasLive, setLastPushWasLive] = useState(false);
  // Demo mode — banner + gate modal when the current user is `is_demo`.
  const isDemo = !!user?.is_demo;
  const demoGate = useDemoGate(isDemo);

  // Subscription gate: paid subscribers, active-pilot users, or ADMIN see all platinum modules.
  const isAdmin = user?.role === "ADMIN";
  const subStatus = user?.subscription?.status;
  const subActive = subStatus === "active" || subStatus === "trialing";
  const plan = user?.subscription?.plan || (isAdmin ? "pro" : null);
  const isPro = plan === "pro";
  const paid = isAdmin || subActive;

  // Modules — tier gates:
  //   Starter: Portfolio (rollup) · Scorecard · S/4HANA · Copilot (chat)
  //   Platinum: + Dynamics 365 · Value · Insights (proactive) + Board Pack
  const VIEWS = [
    { id: "portfolio",  label: "Portfolio",       icon: Layers,     starter: true },
    { id: "scorecard",  label: "Scorecard",       icon: Target,     starter: true },
    { id: "hana",       label: "S/4HANA",         icon: BarChart3,  starter: true },
    { id: "d365",       label: "Dynamics 365",    icon: BarChart3,  starter: false },
    { id: "oracle",     label: "Oracle Cloud",    icon: BarChart3,  starter: false },
    { id: "value",      label: "Value",           icon: TrendingUp, starter: false },
    { id: "insights",   label: "AI Insights",     icon: Sparkles,   starter: false },
    { id: "copilot",    label: "NAVBOT",          icon: Zap,        starter: true },
    { id: "connections",label: "Connections",     icon: Plug,       starter: false },
    { id: "admin",      label: "Admin",           icon: Settings,   starter: true },
  ];

  useEffect(() => {
    csuiteApi.rollup()
      .then(d => { setData(d); setLastUpdatedAt(Date.now()); setLastPushWasLive(false); })
      .catch(e => setErr(e.message || "Failed to load rollup"))
      .finally(() => setBusy(false));
  }, []);

  // Live-push — silently refetch the rollup on any project mutation in the
  // same org. Debounced to a single refetch per 500ms so a rapid burst of
  // updates doesn't hammer the backend.
  const [liveTick, setLiveTick] = useState(0);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [alertToast, setAlertToast] = useState(null); // {project_name, count, at}
  useLiveUpdates((ev) => {
    if (!ev.type) return;
    if (ev.type.startsWith("project.")) setLiveTick((n) => n + 1);
    if (ev.type === "navbot.alert") {
      // Re-fetch the authoritative unread count from the server so the badge
      // stays accurate no matter how many signals were bundled into the alert.
      csuiteApi.aiAlertsUnread?.().then(r => setUnreadAlerts(r?.unread || 0)).catch(() => {});
      setAlertToast({ project_name: ev.project_name, count: ev.count || 1, at: Date.now() });
    }
  });
  useEffect(() => {
    if (liveTick === 0) return;
    const t = setTimeout(() => {
      csuiteApi.rollup()
        .then(d => { setData(d); setLastUpdatedAt(Date.now()); setLastPushWasLive(true); })
        .catch(() => { /* keep last-good */ });
    }, 500);
    return () => clearTimeout(t);
  }, [liveTick]);

  // Initial unread-alert count on load.
  useEffect(() => {
    csuiteApi.aiAlertsUnread?.().then(r => setUnreadAlerts(r?.unread || 0)).catch(() => {});
  }, []);

  // Auto-clear the badge when the user opens the NAVBOT tab.
  useEffect(() => {
    if (view !== "copilot") return;
    if (unreadAlerts === 0) return;
    csuiteApi.aiAlertsMarkRead?.().then(() => setUnreadAlerts(0)).catch(() => {});
  }, [view, unreadAlerts]);

  // Auto-dismiss toast after 8s
  useEffect(() => {
    if (!alertToast) return;
    const t = setTimeout(() => setAlertToast(null), 8000);
    return () => clearTimeout(t);
  }, [alertToast]);

  // Manual dashboard override overlay (edited via Admin → Manual Override)
  const [override, setOverride] = useState({});
  const reloadOverride = () => api.getOverride().then(r => setOverride(r.override || {})).catch(() => {});
  useEffect(() => { reloadOverride(); }, []);

  const rawTotals = data?.totals || {};
  const rawVariance = rawTotals?.variance ?? 0;
  const rawVariancePct = rawTotals && rawTotals.allocated > 0 ? Math.round((rawVariance / rawTotals.allocated) * 100) : 0;
  const rawOnTrack = (() => {
    const h = data?.healthChart || [];
    const total = h.reduce((s, x) => s + x.count, 0) || 1;
    const ok = h.find(x => x.bucket === "On Track")?.count || 0;
    return Math.round((ok / total) * 100);
  })();
  const totals = {
    ...rawTotals,
    avgProgress: override.avg_progress !== undefined ? override.avg_progress : rawTotals.avgProgress,
    criticalRisks: override.critical_risks !== undefined ? override.critical_risks : rawTotals.criticalRisks,
  };
  const variancePct = override.budget_variance_pct !== undefined ? override.budget_variance_pct : rawVariancePct;
  const variance = variancePct;  // sign-only rendering below relies on this
  const onTrack = override.on_track_pct !== undefined ? override.on_track_pct : rawOnTrack;
  const overrideActive = Object.keys(override).some(k => k !== "email" && k !== "updated_at" && override[k] !== undefined && override[k] !== null && override[k] !== "");

  // Constraint breakdown by impact
  const constraintBreakdown = useMemo(() => {
    const arr = data?.constraints || [];
    const counts = { Critical: 0, High: 0 };
    arr.forEach(c => { counts[c.impact] = (counts[c.impact] || 0) + 1; });
    return counts;
  }, [data]);

  if (busy) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--paper)]">
        <Loader2 size={32} className="animate-spin text-[var(--navy)]"/>
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--paper)]">
        <div className="text-red-700 max-w-md text-center">
          <AlertTriangle size={24} className="mx-auto mb-2"/>
          <div>{err}</div>
          <button className="btn btn-primary mt-4" onClick={() => nav("/csuite")}>Back to login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="csuite-cherry min-h-screen text-[var(--tx1)]" data-testid="csuite-dashboard">
      {isDemo && <DemoBanner onUpgrade={() => demoGate.trigger("default")} />}
      {isDemo && <GuidedTour isDemo={isDemo} onNavigate={setView} />}
      {demoGate.modal}

      {/* NAVBOT proactive alert toast */}
      {alertToast && (
        <div
          className="fixed bottom-6 right-6 z-[90] max-w-sm bg-white border-l-4 border-red-600 shadow-2xl rounded-sm p-4 animate-in slide-in-from-bottom-4"
          data-testid="navbot-alert-toast"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full grid place-items-center text-white shrink-0" style={{ background: "linear-gradient(135deg, #7A1F2B 0%, #B03A48 100%)" }}>
              <Sparkles size={14}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[9px] uppercase tracking-widest text-red-700 mb-0.5">NAVBOT · red-signal alert</div>
              <div className="font-display text-sm font-bold text-zinc-900 truncate">
                {alertToast.project_name || "A project"} flipped to Red
              </div>
              <div className="text-xs text-zinc-600 mt-0.5">
                {alertToast.count} new signal{alertToast.count === 1 ? "" : "s"} — NAVBOT has drafted the brief for you.
              </div>
              <button
                onClick={() => { setView("copilot"); setAlertToast(null); }}
                className="mt-2 text-xs font-semibold text-red-700 hover:text-red-900 inline-flex items-center gap-1"
                data-testid="navbot-alert-toast-open"
              >
                Open NAVBOT <ChevronRight size={12}/>
              </button>
            </div>
            <button onClick={() => setAlertToast(null)} className="text-zinc-400 hover:text-zinc-800" data-testid="navbot-alert-toast-dismiss">
              ×
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="border-b border-[var(--border)] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" aria-label="Capittance home"><Logo height={90} /></Link>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Executive Portfolio</div>
              <div className="font-display text-base font-bold tracking-tight">{user?.role} Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">{user?.username}</div>
              <div className="text-xs text-zinc-700">{user?.email}</div>
            </div>
            <span className="px-2 py-1 rounded-sm bg-[var(--navy)] text-white font-mono text-[10px] uppercase tracking-wider" data-testid="csuite-role-pill">
              {user?.role}
            </span>
            <LiveUpdatedPill lastUpdatedAt={lastUpdatedAt} live={lastPushWasLive} />
            {(isPro || isAdmin) && (
              <BoardPackButton />
            )}
            <button className="btn btn-ghost btn-sm" onClick={async () => { await logout(); nav("/csuite", { replace: true }); }} data-testid="csuite-logout">
              <LogOut size={13}/> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Platinum tab bar */}
        <div className="mb-6 border-b border-[var(--border)] overflow-x-auto">
          <div className="flex items-end gap-1 min-w-max">
            {VIEWS.map(v => {
              const gated = !v.starter && !isPro && !isAdmin;
              const Icon = v.icon;
              const showBadge = v.id === "copilot" && unreadAlerts > 0;
              return (
                <button
                  key={v.id}
                  onClick={() => setView(v.id)}
                  className={`px-4 py-2.5 flex items-center gap-2 text-sm font-medium border-b-2 -mb-px transition-colors relative ${
                    view === v.id
                      ? "border-[var(--navy)] text-[var(--navy)]"
                      : "border-transparent text-zinc-500 hover:text-zinc-800"
                  }`}
                  data-testid={`csuite-tab-${v.id}`}
                >
                  <Icon size={14}/>
                  {v.label}
                  {gated && <span className="ml-1 pill pill-neutral text-[9px]">PLATINUM</span>}
                  {showBadge && (
                    <span
                      className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold animate-pulse"
                      title={`${unreadAlerts} unread NAVBOT red-signal alert${unreadAlerts === 1 ? "" : "s"}`}
                      data-testid="navbot-alert-badge"
                    >
                      {unreadAlerts}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Paywall — anyone without an active subscription (and not ADMIN) sees this instead of any tab. */}
        {!paid && <PaywallCard />}

        {paid && view === "portfolio" && (<>
        <PilotStatusBanner />
        {overrideActive && (
          <div className="mb-3 p-2 rounded-sm bg-amber-50 border border-amber-200 text-xs text-amber-900 inline-flex items-center gap-2" data-testid="override-active">
            <span className="pill pill-amber text-[9px]">MANUAL OVERRIDE</span>
            Some KPIs are showing values you entered in Admin → Manual Override.
            {override.note && <span className="italic">· “{override.note}”</span>}
          </div>
        )}
        <div className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Welcome back, {user?.username}</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-zinc-900 mt-1">
            Program Portfolio — Executive Rollup
          </h1>
          <p className="text-sm text-zinc-600 mt-2">
            Live aggregation across {totals?.projects} active project{totals?.projects === 1 ? "" : "s"}. Refreshed when you load the page.
          </p>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <KPI testid="kpi-progress" label="Avg Progress" value={`${totals?.avgProgress || 0}%`} sub={`${totals?.projects} projects`} tone="blue" icon={<Activity size={14}/>}/>
          <KPI testid="kpi-on-track" label="On Track %"   value={`${onTrack}%`} sub={`${data?.healthChart?.find(x => x.bucket === "On Track")?.count || 0} of ${totals?.projects}`} tone={onTrack >= 70 ? "green" : onTrack >= 50 ? "amber" : "red"} icon={<Gauge size={14}/>}/>
          <KPI testid="kpi-variance"  label="Budget Variance" value={`${variance >= 0 ? "+" : ""}${variancePct}%`} sub={`Forecast vs allocated`} tone={variance <= 0 ? "green" : "red"} icon={<DollarSign size={14}/>}/>
          <KPI testid="kpi-risks"     label="Critical Risks"  value={totals?.criticalRisks || 0} sub={`${totals?.openRisks || 0} open total`} tone={(totals?.criticalRisks || 0) > 0 ? "red" : "green"} icon={<AlertTriangle size={14}/>}/>
        </div>

        {/* Charts: Pie + Histogram */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
          <div className="bg-white border border-[var(--border)] rounded-sm p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-3">Overall Pie — by Project Phase</div>
            <div style={{ width: "100%", height: 280, minHeight: 280 }}>
              <ResponsiveContainer minWidth={0} minHeight={0}>
                <PieChart>
                  <Pie data={data?.phaseChart || []} dataKey="count" nameKey="phase"
                       outerRadius={100} innerRadius={55} paddingAngle={2}>
                    {(data?.phaseChart || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>)}
                    <Label value={`${totals?.projects}`} position="center" className="font-display" style={{ fontSize: 28, fontWeight: 800, fill: "#0F172A" }}/>
                  </Pie>
                  <Tooltip contentStyle={{ background: "white", border: "1px solid #E0F2FE", fontSize: 12 }}/>
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle"/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-[var(--border)] rounded-sm p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-3">Histogram — Overall Program Status</div>
            <div style={{ width: "100%", height: 280, minHeight: 280 }}>
              <ResponsiveContainer minWidth={0} minHeight={0}>
                <BarChart data={data?.healthChart || []} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0F2FE"/>
                  <XAxis dataKey="bucket" stroke="#64748B" fontSize={11}/>
                  <YAxis allowDecimals={false} stroke="#64748B" fontSize={11}/>
                  <Tooltip contentStyle={{ background: "white", border: "1px solid #E0F2FE", fontSize: 12 }}/>
                  <Bar dataKey="count" radius={[4,4,0,0]}>
                    {(data?.healthChart || []).map((b, i) => <Cell key={i} fill={HEALTH_COLORS[b.bucket] || "#0369A1"}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Financials */}
        <Section eyebrow="Finance" title="Financial Details">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="csuite-financials">
            <KPI testid="kpi-alloc"    label="Total Allocated" value={fmtMoney(totals?.allocated)} sub="Across portfolio" tone="blue"/>
            <KPI testid="kpi-actual"   label="Actual Spend"    value={fmtMoney(totals?.actual)} sub={`${totals?.allocated ? Math.round((totals.actual / totals.allocated) * 100) : 0}% of allocated`} tone="neutral"/>
            <KPI testid="kpi-forecast" label="Forecast"        value={fmtMoney(totals?.forecast)} sub="Projected total"   tone={(totals?.forecast || 0) > (totals?.allocated || 0) ? "red" : "green"}/>
            <KPI testid="kpi-var-abs"  label="Variance"        value={`${variance >= 0 ? "+" : ""}${fmtMoney(Math.abs(variance))}`} sub={variance <= 0 ? "Under budget" : "Over forecast"} tone={variance <= 0 ? "green" : "red"}/>
          </div>
        </Section>

        {/* EBITDA + ROI */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
          <Section eyebrow="Value" title="EBITDA Snapshot">
            <ValueTable
              rows={data?.ebitda || []}
              colKey="ebitda"
              colLabel="Impact"
              emptyHint="Add an 'EBITDA Impact (3yr)' KPI under the S/4HANA tab to surface it here."
              testid="csuite-ebitda"
            />
          </Section>
          <Section eyebrow="Value" title="ROI by Project">
            <ValueTable
              rows={data?.roi || []}
              colKey="roi"
              colLabel="ROI"
              emptyHint="Open any project → Settings → set a ROI value in meta."
              testid="csuite-roi"
            />
          </Section>
        </div>

        {/* Progress + Constraints */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
          <Section eyebrow="Delivery" title="Progress Status by Project" right={<div className="font-mono text-[10px] text-zinc-500">click → drill-in</div>}>
            <div className="bg-white border border-[var(--border)] rounded-sm overflow-hidden" data-testid="csuite-progress-list">
              <table className="w-full text-sm">
                <thead className="bg-[var(--surface)] border-b border-[var(--border)]">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Project</th>
                    <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Phase</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Progress</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Health</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.projects || []).map((p) => (
                    <tr key={p.id} className="border-b border-[var(--border)] last:border-0 hover:bg-zinc-50 cursor-pointer"
                        onClick={() => nav(`/csuite/project/${p.id}`)} data-testid={`csuite-project-row-${p.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-zinc-900">{p.name}</div>
                        <div className="font-mono text-[10px] text-zinc-500">{p.code}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-700">{p.phase || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        <div className="flex items-center gap-2 justify-end">
                          <div className="h-1.5 w-20 bg-zinc-100 rounded-sm overflow-hidden">
                            <div className="h-full" style={{ width: `${p.progress}%`, background: "#0369A1" }}/>
                          </div>
                          {p.progress}%
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Pill tone={p.healthBucket === "On Track" ? "green" : p.healthBucket === "At Risk" ? "amber" : p.healthBucket === "Off Track" ? "red" : "neutral"}>{p.healthBucket}</Pill>
                      </td>
                      <td className="px-2 py-2 text-zinc-400"><ChevronRight size={14}/></td>
                    </tr>
                  ))}
                  {(data?.projects || []).length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-500 italic">No projects yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Section>

          <Section
            eyebrow="Risk"
            title="Constraints"
            right={
              <div className="flex items-center gap-2 font-mono text-[10px] text-zinc-500">
                <span><span className="text-red-600 font-bold">{constraintBreakdown.Critical || 0}</span> critical</span>
                <span><span className="text-amber-600 font-bold">{constraintBreakdown.High || 0}</span> high</span>
              </div>
            }>
            <div className="bg-white border border-[var(--border)] rounded-sm" data-testid="csuite-constraints-list">
              {(data?.constraints || []).map((c, i) => (
                <div key={i} className="border-b border-[var(--border)] last:border-0 p-3 flex items-start gap-3">
                  <div className={`w-1 self-stretch rounded-sm ${c.impact === "Critical" ? "bg-red-600" : "bg-amber-500"}`}/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Pill tone={c.impact === "Critical" ? "red" : "amber"}>{c.impact}</Pill>
                      <span className="font-mono text-[10px] text-zinc-500">{c.type}</span>
                      <span className="font-mono text-[10px] text-zinc-500">· {c.project}</span>
                    </div>
                    <div className="text-sm text-zinc-900 mt-1 font-medium">{c.title}</div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-0.5">Owner: {c.owner || "—"} · Status: {c.status || "—"}</div>
                  </div>
                </div>
              ))}
              {(data?.constraints || []).length === 0 && (
                <div className="p-6 text-center text-zinc-500 italic text-sm">No active critical or high-impact constraints across the portfolio.</div>
              )}
            </div>
          </Section>
        </div>

        <div className="mt-8">
          <ExecutiveDiagnosticsCard />
        </div>

        <div className="mt-8">
          <ReferralsCard variant="dashboard" />
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-1 gap-6">
          <SwitchPlanCard />
        </div>

        <div className="mt-8 text-center">
          <Link to="/projects" className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400 hover:text-zinc-700">
            Switch to operator workspace →
          </Link>
        </div>
        </>)}

        {paid && view === "scorecard" && <Scorecard />}
        {paid && view === "hana"      && <HanaCockpit />}
        {paid && view === "d365"      && (isPro || isAdmin ? <D365Cockpit /> : <TierUpgrade module="Dynamics 365" />)}
        {paid && view === "oracle"    && (isPro || isAdmin ? <OracleCockpit /> : <TierUpgrade module="Oracle Cloud ERP" />)}
        {paid && view === "value"     && (isPro || isAdmin ? <ValueRealization /> : <TierUpgrade module="Value Realization" />)}
        {paid && view === "insights"  && (isPro || isAdmin ? <InsightsFeed /> : <TierUpgrade module="Proactive AI Insights" />)}
        {paid && view === "copilot"   && <AICopilot />}
        {paid && view === "connections" && (isPro || isAdmin ? <ConnectionsPanel /> : <TierUpgrade module="Live Connections" />)}
        {paid && view === "admin"       && <EnterprisePanel onDataChanged={() => { csuiteApi.rollup().then(setData).catch(() => {}); reloadOverride(); }} />}

        <div className="mt-16 pt-6 border-t border-[var(--border)] flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex-wrap gap-2">
          <div>© Capittance 2026 · Executive Portal</div>
          <div>Support · <a href="mailto:Requests@Capittance.com?subject=Executive%20Portal%20request" className="underline hover:no-underline" data-testid="csuite-support-link">Requests@Capittance.com</a></div>
        </div>
      </main>
    </div>
  );
}

// Simple in-panel upsell when a Starter user opens a Pro-only module.
function TierUpgrade({ module }) {
  return (
    <div className="card p-8 text-center max-w-lg mx-auto" data-testid="tier-upgrade">
      <Sparkles size={28} className="mx-auto mb-2" style={{ color: "#B03A48" }}/>
      <div className="font-display text-xl font-extrabold">{module} is a Platinum feature</div>
      <div className="text-sm text-zinc-500 mt-1 mb-4">Upgrade from Starter ($200/mo) to Platinum ($350/mo) to unlock this module plus proactive AI briefings and the Board Pack generator.</div>
      <Link to="/signup" className="btn btn-primary" data-testid="tier-upgrade-btn">Upgrade to Platinum →</Link>
    </div>
  );
}

function ValueTable({ rows, colKey, colLabel, emptyHint, testid }) {
  return (
    <div className="bg-white border border-[var(--border)] rounded-sm" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-[var(--surface)] border-b border-[var(--border)]">
          <tr>
            <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Project</th>
            <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">{colLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[var(--border)] last:border-0">
              <td className="px-3 py-2 text-zinc-800">{r.project}</td>
              <td className="px-3 py-2 text-right font-mono font-semibold text-zinc-900">{r[colKey]}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={2} className="px-3 py-5 text-center italic text-zinc-500 text-xs">{emptyHint}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
